import { useRef, useState } from "react";
import Papa from "papaparse";
import { Export } from "../../assets/icons";

const DEFAULT_LABEL = "Importer un fichier ou glisser-déposer. CSV jusqu'à 1MB.";

const validateFile = (file, { maxSizeMB, accept }) => {
  if (!file) return { code: "wrong_type", message: "Aucun fichier" };
  const ext = (file.name || "").toLowerCase().split(".").pop();
  const acceptedExts = (accept || "")
    .split(",")
    .map((s) => s.trim().replace(/^\./, "").toLowerCase())
    .filter((s) => s.length > 0 && !s.includes("/"));
  if (acceptedExts.length > 0 && !acceptedExts.includes(ext)) {
    if (
      !(file.type || "")
        .toLowerCase()
        .includes("csv")
    ) {
      return { code: "wrong_type", message: "Format de fichier invalide" };
    }
  }
  if (file.size > maxSizeMB * 1024 * 1024) {
    return {
      code: "too_large",
      message: `Le fichier dépasse ${maxSizeMB}Mo`,
    };
  }
  return null;
};

// Reusable CSV drag & drop zone. The component reads the file, parses it via PapaParse
// and exposes either the rows or an error to its parent. Stateless wrt API : the caller
// decides what to do with the parsed rows.
const CsvDropzone = ({
  onParsed,
  onError,
  maxSizeMB = 1,
  accept = ".csv,text/csv",
  disabled = false,
  papaParseConfig = { skipEmptyLines: true },
  dropzoneLabel = DEFAULT_LABEL,
}) => {
  const inputRef = useRef(null);
  const [state, setState] = useState("idle"); // idle | hover | parsing | done | error
  const [fileName, setFileName] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const reset = () => {
    setState("idle");
    setFileName("");
    setErrorMsg("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const triggerOpen = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleFile = (file) => {
    const error = validateFile(file, { maxSizeMB, accept });
    if (error) {
      setState("error");
      setErrorMsg(error.message);
      onError?.(error);
      return;
    }

    setFileName(file.name);
    setState("parsing");
    Papa.parse(file, {
      ...papaParseConfig,
      complete: (results) => {
        setState("done");
        onParsed?.(results.data || [], file);
      },
      error: (err) => {
        setState("error");
        setErrorMsg(err?.message || "Erreur de lecture du CSV");
        onError?.({ code: "parse_error", message: err?.message || "" });
      },
    });
  };

  const onDragOver = (event) => {
    event.preventDefault();
    if (disabled) return;
    if (state === "idle") setState("hover");
  };

  const onDragLeave = () => {
    if (state === "hover") setState("idle");
  };

  const onDrop = (event) => {
    event.preventDefault();
    if (disabled) return;
    const file = event.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const onChange = (event) => {
    const file = event.target.files?.[0];
    if (file) handleFile(file);
  };

  const baseClass =
    "flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed py-8 px-4 text-center transition-colors";
  const stateClass = {
    idle: "border-gray-300 bg-white hover:bg-gray-50 cursor-pointer",
    hover: "border-workfully-700 bg-workfully-50 cursor-copy",
    parsing: "border-gray-300 bg-gray-50 cursor-progress",
    done: "border-green-500 bg-green-50",
    error: "border-red-500 bg-red-50",
  }[state];

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={state === "idle" || state === "hover" ? triggerOpen : undefined}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && state === "idle") triggerOpen();
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`${baseClass} ${stateClass} ${disabled ? "opacity-60 pointer-events-none" : ""}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={onChange}
        disabled={disabled}
      />
      {state === "idle" || state === "hover" ? (
        <>
          <Export wh={36} color="#9ca3af" />
          <div className="ft-sm text-gray-600">{dropzoneLabel}</div>
        </>
      ) : state === "parsing" ? (
        <div className="ft-sm text-gray-600">Lecture de {fileName}...</div>
      ) : state === "done" ? (
        <div className="flex flex-col items-center gap-2">
          <div className="ft-sm text-green-700">{fileName} importé</div>
          <button
            type="button"
            className="ft-sm text-gray-600 underline"
            onClick={(e) => {
              e.stopPropagation();
              reset();
            }}
          >
            Retirer
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <div className="ft-sm text-red-600">{errorMsg}</div>
          <button
            type="button"
            className="ft-sm text-gray-600 underline"
            onClick={(e) => {
              e.stopPropagation();
              reset();
            }}
          >
            Réessayer
          </button>
        </div>
      )}
    </div>
  );
};

export default CsvDropzone;
